import {
  StatsigProvider as BaseStatsigProvider,
  useClientAsyncInit,
} from '@statsig/react-bindings'
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics'
import { useEffect } from 'react'
import { StatsigClarityTracking } from './StatsigClarityTracking'
import { useShopify } from '@/hooks/useShopify'
import { useShouldEnableExperimentation } from '@/features/experimentation/hooks/useShouldEnableExperimentation'
import { useQueryFlags } from '@/hooks/useQueryFlags'

const ExperimentationProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const shouldEnableExperimentation = useShouldEnableExperimentation()

  if (!shouldEnableExperimentation) {
    return <>{children}</>
  }

  return <StatsigProvider>{children}</StatsigProvider>
}

const StatsigProvider = ({ children }: { children: React.ReactNode }) => {
  const { environment, integrations } = useShopify()

  const { queryFlags } = useQueryFlags()

  const statsigEnvironment: string = import.meta.env.DEV
    ? 'development'
    : environment && environment === 'production'
      ? 'production'
      : 'staging'

  const { client } = useClientAsyncInit(
    integrations.statsig_client_key,
    {
      locale: navigator.language,
      custom: {
        hostname: window.location.hostname,
        queryFlags,
      },
    },
    {
      plugins: [new StatsigAutoCapturePlugin()],
      environment: {
        tier: statsigEnvironment,
      },
    },
  )

  useEffect(() => {
    if (!client) return

    const clarityTracking = new StatsigClarityTracking(client)

    clarityTracking.startTracking()

    return () => {
      clarityTracking.stopTracking()
    }
  }, [client])

  return <BaseStatsigProvider client={client}>{children}</BaseStatsigProvider>
}

export default ExperimentationProvider
